import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useGetNeuralNetworkLogsQuery } from '@app/api/NeuralNetworkLogs';
import type {
  GetNeuralNetworkLogsParams,
  ModelNeuralNetworkLogsResult,
} from '@app/api/NeuralNetworkLogs';
import { useUserPrint } from '@app/hooks';
import { useErrorHandler } from '@app/shared/hooks';

import {
  initPagination,
  getNeuralNetworkLogsColumns,
} from '@pages/NeuralNetworkLogs/data';
import type {
  FilterChangeType,
  Pagination,
} from '@pages/NeuralNetworkLogs/types';

import type { PaginationProps } from 'antd';

const defaultDates = {
  DateS: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
  DateE: dayjs().format('YYYY-MM-DD'),
};

export const useNeuralNetworkLogs = () => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();

  const [filters, setFilters] =
    useState<GetNeuralNetworkLogsParams>(defaultDates);
  const [unsavedFilters, setUnsavedFilters] =
    useState<GetNeuralNetworkLogsParams>({});

  const [pagination, setPagination] = useState<Pagination>(
    () => initPagination
  );
  const { page, itemsPerPage } = pagination;

  const [currentLog, setCurrentLog] =
    useState<ModelNeuralNetworkLogsResult | null>(null);
  const isLogModalOpen = useMemo(() => !!currentLog, [currentLog]);
  const openLog = useCallback((log: ModelNeuralNetworkLogsResult) => {
    setCurrentLog(log);
  }, []);
  const closeLog = useCallback(() => setCurrentLog(null), []);

  const {
    data: neuralNetworkLogsData,
    isLoading: isNeuralNetworkLogsLoading,
    isFetching: isNeuralNetworkLogsFetching,
    isUninitialized: isNeuralNetworkLogsUninitialized,
    error: neuralNetworkLogsError,
    isError: isNeuralNetworkLogsError,
    refetch: refetchNeuralNetworkLogs,
  } = useGetNeuralNetworkLogsQuery(
    { ActiveBranchId, ...filters },
    { skip: !ActiveBranchId }
  );

  useErrorHandler([
    { flag: isNeuralNetworkLogsError, error: neuralNetworkLogsError },
  ]);

  useEffect(() => {
    if (!unsavedFilters.DateS && !unsavedFilters.DateE) {
      setUnsavedFilters(defaultDates);
    }
  }, [unsavedFilters.DateE, unsavedFilters.DateS]);

  const isPageLoading = useMemo(
    () => isNeuralNetworkLogsLoading || isNeuralNetworkLogsUninitialized,
    [isNeuralNetworkLogsLoading, isNeuralNetworkLogsUninitialized]
  );

  const isFetching = useMemo(
    () => isNeuralNetworkLogsFetching,
    [isNeuralNetworkLogsFetching]
  );

  const onFilterChange: FilterChangeType = useCallback((key, value) => {
    setUnsavedFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const onReset = useCallback(() => {
    setUnsavedFilters({});
    setFilters({});
    setPagination(initPagination);
  }, []);

  const onSearch = useCallback(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setFilters((prev) => {
      if (
        unsavedFilters.DateS === prev.DateS &&
        unsavedFilters.DateE === prev.DateE
      ) {
        refetchNeuralNetworkLogs();
      }
      return unsavedFilters;
    });
  }, [unsavedFilters, refetchNeuralNetworkLogs]);

  const onPaginate: PaginationProps['onChange'] = useCallback(
    (nextPage: number, pageSize: number) => {
      setPagination((prev) => ({
        ...prev,
        page: nextPage,
        itemsPerPage: pageSize,
      }));
    },
    []
  );

  const columns = useMemo(
    () => getNeuralNetworkLogsColumns({ openLog }),
    [openLog]
  );

  const total = useMemo(
    () => neuralNetworkLogsData?.length ?? 0,
    [neuralNetworkLogsData]
  );

  useEffect(() => {
    if (total === 0) return;
    const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
    if (page > totalPages) {
      setPagination((prev) => ({ ...prev, page: totalPages }));
    }
  }, [total, itemsPerPage, page]);

  const pageData = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return (neuralNetworkLogsData ?? []).slice(start, end);
  }, [neuralNetworkLogsData, page, itemsPerPage]);

  const viewPagination: Pagination = useMemo(
    () => ({ ...pagination, total }),
    [pagination, total]
  );

  return {
    isPageLoading,
    onFilterChange,
    onSearch,
    pagination: viewPagination,
    neuralNetworkLogsData: pageData,
    onPaginate,
    onReset,
    isFetching,
    columns,
    isLogModalOpen,
    currentLog,
    closeLog,
    unsavedFilters,
  };
};
